import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { volunteerService } from '@services/volunteer.service';
import { locationService } from '@services/location.service';
import { useCampaignStore } from '@stores/campaign.store';
import { useAnalytics } from '@hooks/useAnalytics';
import type { VolunteerResponse } from '@services/volunteer.service';
import type { LocationItem } from '@/types';
import codeOfConductPdf from '../assets/docs/code_of_conduct.pdf';

export function Volunteer() {
  const { t } = useTranslation();
  const { trackVolunteerSignup, trackFormSubmission } = useAnalytics();
  const [agreedToCodeOfConduct, setAgreedToCodeOfConduct] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    districtId: '',
    subDistrictId: '',
    sucoId: '',
    aldeiaId: '',
    interests: [] as string[],
    message: '',
  });

  const interestOptions = [
    { value: 'Door-to-Door', label: 'Door-to-Door Canvassing', tooltipKey: 'volunteer.interestTooltips.doorToDoor' },
    { value: 'Mobilization', label: 'Event Mobilization', tooltipKey: 'volunteer.interestTooltips.mobilization' },
    { value: 'Donor', label: 'Donor Outreach', tooltipKey: 'volunteer.interestTooltips.donor' },
    { value: 'Social Media', label: 'Social Media & Communications', tooltipKey: 'volunteer.interestTooltips.socialMedia' },
    { value: 'Logistics', label: 'Logistics & Operations', tooltipKey: 'volunteer.interestTooltips.logistics' },
    { value: 'Data Entry', label: 'Data Entry & Administration', tooltipKey: 'volunteer.interestTooltips.dataEntry' },
  ];
  
  // Location data
  const [districts, setDistricts] = useState<LocationItem[]>([]);
  const [subDistricts, setSubDistricts] = useState<LocationItem[]>([]);
  const [sucos, setSucos] = useState<LocationItem[]>([]);
  const [aldeias, setAldeias] = useState<LocationItem[]>([]);
  
  // Loading states
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { addNotification } = useCampaignStore();

  // Load districts on component mount
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const data = await locationService.withRetry(() => locationService.getDistricts());
        setDistricts(data);
      } catch (error) {
        console.error('Failed to load districts:', error);
        addNotification({
          type: 'error',
          message: 'Failed to load location data. Please refresh the page.',
        });
      }
    };
    loadDistricts();
  }, [addNotification]);

  // Handle district change - load sub-districts
  const handleDistrictChange = async (districtId: string) => {
    setFormData({
      ...formData,
      districtId,
      subDistrictId: '',
      sucoId: '',
      aldeiaId: '',
    });
    setSubDistricts([]);
    setSucos([]);
    setAldeias([]);

    if (districtId) {
      try {
        setIsLoadingLocations(true);
        const data = await locationService.withRetry(() => locationService.getSubDistricts(districtId));
        setSubDistricts(data);
      } catch (error) {
        console.error('Failed to load sub-districts:', error);
      } finally {
        setIsLoadingLocations(false);
      }
    }
  };

  // Handle sub-district change - load sucos
  const handleSubDistrictChange = async (subDistrictId: string) => {
    setFormData({
      ...formData,
      subDistrictId,
      sucoId: '',
      aldeiaId: '',
    });
    setSucos([]);
    setAldeias([]);

    if (subDistrictId) {
      try {
        setIsLoadingLocations(true);
        const data = await locationService.withRetry(() => locationService.getSucos(subDistrictId));
        setSucos(data);
      } catch (error) {
        console.error('Failed to load sucos:', error);
      } finally {
        setIsLoadingLocations(false);
      }
    }
  };

  // Handle suco change - load aldeias
  const handleSucoChange = async (sucoId: string) => {
    setFormData({
      ...formData,
      sucoId,
      aldeiaId: '',
    });
    setAldeias([]);

    if (sucoId) {
      try {
        setIsLoadingLocations(true);
        const data = await locationService.withRetry(() => locationService.getAldeias(sucoId));
        setAldeias(data);
      } catch (error) {
        console.error('Failed to load aldeias:', error);
      } finally {
        setIsLoadingLocations(false);
      }
    }
  };

  // Handle aldeia change
  const handleAldeiaChange = (aldeiaId: string) => {
    setFormData({
      ...formData,
      aldeiaId,
    });
  };

  // Handle interest toggle
  const handleInterestToggle = (interestValue: string) => {
    const currentInterests = [...formData.interests];
    const index = currentInterests.indexOf(interestValue);
    
    if (index > -1) {
      // Remove if already selected
      currentInterests.splice(index, 1);
    } else {
      // Add if not selected
      currentInterests.push(interestValue);
    }
    
    setFormData({
      ...formData,
      interests: currentInterests,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.interests.length === 0) {
      addNotification({
        type: 'error',
        message: 'Please select at least one area of interest.',
      });
      trackFormSubmission('volunteer_signup', false);
      return;
    }

    if (!agreedToCodeOfConduct) {
      addNotification({
        type: 'error',
        message: 'Please agree to the Code of Conduct before submitting.',
      });
      trackFormSubmission('volunteer_signup', false);
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert interests array to comma-delimited string for API
      const payload = {
        ...formData,
        interest: formData.interests.join(','),
      };

      const result: VolunteerResponse = await volunteerService.signup(payload);
      console.log('Volunteer registered successfully:', result);

      // Track successful signup
      trackVolunteerSignup(formData.districtId || 'unknown');
      trackFormSubmission('volunteer_signup', true);

      const successMessage = result.message || t('volunteer.success');
      console.log('Showing success notification:', successMessage);

      addNotification({
        type: 'success',
        message: successMessage,
      });

      // Reset form on success
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        districtId: '',
        subDistrictId: '',
        sucoId: '',
        aldeiaId: '',
        interests: [],
        message: '',
      });
      setSubDistricts([]);
      setSucos([]);
      setAldeias([]);
      setAgreedToCodeOfConduct(false);
    } catch (error) {
      console.error('Volunteer signup error:', error);
      
      // Track failed signup
      trackFormSubmission('volunteer_signup', false);
      
      let errorMessage = t('volunteer.error');

      // Handle Axios errors
      if (error instanceof AxiosError) {
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);

        const status = error.response?.status;
        const data = error.response?.data as { message?: string; errors?: Record<string, string[]>; title?: string };

        if (status === 400) {
          if (data?.errors) {
            // Validation errors - show first error message
            const firstError = Object.values(data.errors)[0]?.[0];
            errorMessage = firstError || errorMessage;
          } else if (data?.title) {
            // RFC 7807 Problem Details
            errorMessage = data.title;
          }
        } else if (status === 409) {
          // Conflict - email already registered
          errorMessage = data?.message || 'This email is already registered. We will contact you soon!';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      addNotification({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-black text-campaign-black mb-4">
            {t('volunteer.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('volunteer.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label={t('volunteer.firstName')}
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
              <Input
                label={t('volunteer.lastName')}
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </div>

            <Input
              label={t('volunteer.email')}
              type="email"              
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />

            <Input
              label={t('volunteer.phone')}
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />

            <div>
              <label className="block text-sm font-semibold mb-2 text-campaign-black">
                Municipio
              </label>
              <select
                required
                value={formData.districtId}
                onChange={(e) => handleDistrictChange(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-sky-600 focus:outline-none"
              >
                <option value="">Select Municipio</option>
                {districts.map(d => (
                  <option key={d.id} value={d.id}>{d.nameLocal || d.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-campaign-black">
                Posto Administrativo
              </label>
              <select
                required
                value={formData.subDistrictId}
                onChange={(e) => handleSubDistrictChange(e.target.value)}
                disabled={!formData.districtId || isLoadingLocations}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-sky-600 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Posto Administrativo</option>
                {subDistricts.map(sd => (
                  <option key={sd.id} value={sd.id}>{sd.nameLocal || sd.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-campaign-black">
                Suco
              </label>
              <select
                required
                value={formData.sucoId}
                onChange={(e) => handleSucoChange(e.target.value)}
                disabled={!formData.subDistrictId || isLoadingLocations}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-sky-600 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Suco</option>
                {sucos.map(s => (
                  <option key={s.id} value={s.id}>{s.nameLocal || s.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-campaign-black">
                Aldeia
              </label>
              <select
                required
                value={formData.aldeiaId}
                onChange={(e) => handleAldeiaChange(e.target.value)}
                disabled={!formData.sucoId || isLoadingLocations}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-sky-600 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Aldeia</option>
                {aldeias.map(a => (
                  <option key={a.id} value={a.id}>{a.nameLocal || a.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-3 text-campaign-black">
                {t('volunteer.interest')}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {interestOptions.map((option) => {
                  const isSelected = formData.interests.includes(option.value);
                  return (
                    <div key={option.value} className="relative group">
                      <button
                        type="button"
                        onClick={() => handleInterestToggle(option.value)}
                        className={`
                          w-full px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all duration-200
                          ${isSelected
                            ? 'border-sky-600 bg-sky-50 text-sky-700 shadow-sm'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                            ${isSelected ? 'border-sky-600 bg-sky-600' : 'border-gray-300'}
                          `}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span>{option.label}</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </button>
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 whitespace-normal max-w-xs pointer-events-none">
                        {t(option.tooltipKey)}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {formData.interests.length > 0 && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected: {formData.interests.join(', ')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-campaign-black">
                {t('volunteer.message')}
              </label>
              <textarea
                required
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-sky-600 focus:outline-none resize-none"
                placeholder={t('volunteer.messagePlaceholder')}
              />
            </div>

            <div className="border-t-2 border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-campaign-black mb-3">
                Code of Conduct
              </h3>
              <div className="flex items-start gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => setShowPdfPreview(true)}
                  className="text-sky-600 hover:text-sky-700 underline text-sm font-medium"
                >
                  View Kódigu Konduta ba Voluntáriu (PDF)
                </button>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToCodeOfConduct}
                  onChange={(e) => setAgreedToCodeOfConduct(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-sky-600 focus:ring-sky-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700">
                  I have read and agree to the Code of Conduct for Volunteers
                </span>
              </label>
            </div>

            <Button 
              type="submit" 
              isLoading={isSubmitting} 
              disabled={!agreedToCodeOfConduct}
              className="w-full"
            >
              {t('volunteer.submit')}
            </Button>
          </form>
        </div>

        {/* PDF Preview Modal */}
        {showPdfPreview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-campaign-black">
                  Kódigu Konduta ba Voluntáriu (Code of Conduct)
                </h2>
                <button
                  type="button"
                  onClick={() => setShowPdfPreview(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="flex-1 p-4">
                <iframe
                  src={codeOfConductPdf}
                  className="w-full h-full border-0"
                  title="Code of Conduct PDF"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}